import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { MapContainer, TileLayer, Marker, Popup, Polyline, CircleMarker, Tooltip, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Single seam for map rendering. REACT_APP_MAP_PROVIDER: "auto" (Google when REACT_APP_GOOGLE_MAPS_API_KEY is set), "google" or "osm".
// Any Google load failure falls back to Leaflet/OSM. The browser key is public by nature: restrict it by HTTP referrer in Google Cloud.
export const DEFAULT_CENTER = [32.7767, -96.797];
const GKEY = (process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "").trim();
const MODE = (process.env.REACT_APP_MAP_PROVIDER || "auto").toLowerCase();
export const GOOGLE_ENABLED = Boolean(GKEY) && (MODE === "auto" || MODE === "google");

let gPromise;
export const loadGoogle = () => {
  if (!GOOGLE_ENABLED) return Promise.reject(new Error("google maps disabled"));
  if (window.google?.maps?.places) return Promise.resolve(window.google);
  if (!gPromise) {
    gPromise = new Promise((resolve, reject) => {
      window.__luxGmapsReady = () => resolve(window.google);
      window.gm_authFailure = () => reject(new Error("google maps auth failure"));
      const s = document.createElement("script");
      s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(GKEY)}&libraries=places,visualization&v=weekly&callback=__luxGmapsReady`;
      s.async = true;
      s.onerror = () => reject(new Error("google maps script failed"));
      document.head.appendChild(s);
      setTimeout(() => reject(new Error("google maps load timeout")), 12000);
    }).catch((e) => { console.warn("Google Maps unavailable, using OpenStreetMap:", e.message); gPromise = Promise.reject(e); throw e; });
  }
  return gPromise;
};

const DARK_GOLD = [
  { elementType: "geometry", stylers: [{ color: "#121216" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#a1a1aa" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#0a0a0c" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#26262c" }] },
  { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#2f2a1d" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#5c4a1c" }] },
  { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#d4af37" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#0b1220" }] },
  { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#3f3f46" }] },
];

const GCtx = createContext(null);
const useG = () => useContext(GCtx);

const OsmMap = ({ center, zoom, className, style, children }) => (
  <MapContainer center={center} zoom={zoom} scrollWheelZoom className={className} style={style}>
    <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
    {children}
  </MapContainer>
);

const GoogleMap = ({ center, zoom, className, style, children, onFail }) => {
  const el = useRef(null);
  const [ctx, setCtx] = useState(null);
  useEffect(() => {
    let alive = true;
    loadGoogle().then((g) => {
      if (!alive || !el.current) return;
      const map = new g.maps.Map(el.current, { center: { lat: center[0], lng: center[1] }, zoom, styles: DARK_GOLD, backgroundColor: "#0a0a0c",
        disableDefaultUI: true, zoomControl: true, gestureHandling: "greedy", clickableIcons: false });
      setCtx({ g, map, info: new g.maps.InfoWindow() });
    }).catch(() => alive && onFail());
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <div ref={el} className={className} style={style} data-map-provider="google">{ctx && <GCtx.Provider value={ctx}>{children}</GCtx.Provider>}</div>;
};

export const BaseMap = (props) => {
  const [failed, setFailed] = useState(false);
  return GOOGLE_ENABLED && !failed ? <GoogleMap {...props} onFail={() => setFailed(true)} /> : <OsmMap {...props} />;
};

const openPopup = (ctx, content, position) => {
  if (!content) return;
  const node = document.createElement("div");
  node.style.color = "#0a0a0c";
  createRoot(node).render(content);
  ctx.info.setContent(node);
  ctx.info.setPosition({ lat: position[0], lng: position[1] });
  ctx.info.open({ map: ctx.map });
};

const useGoogleObject = (make, deps) => {
  const ctx = useG();
  const obj = useRef(null);
  useEffect(() => {
    if (!ctx) return undefined;
    obj.current = make(ctx);
    return () => { obj.current?.setMap(null); obj.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx, ...deps]);
};

// icon = { html, size: [w, h], anchor: [x, y] }
const GPin = ({ position, icon, children }) => {
  const content = useRef(children);
  content.current = children;
  useGoogleObject(({ g, map, ...ctx }) => {
    const ov = new g.maps.OverlayView();
    const div = document.createElement("div");
    div.style.position = "absolute";
    div.innerHTML = icon.html;
    if (content.current) { div.style.cursor = "pointer"; div.addEventListener("click", () => openPopup({ g, map, ...ctx }, content.current, position)); }
    ov.onAdd = () => ov.getPanes().overlayMouseTarget.appendChild(div);
    ov.draw = () => {
      const p = ov.getProjection()?.fromLatLngToDivPixel(new g.maps.LatLng(position[0], position[1]));
      if (p) { div.style.left = `${p.x - icon.anchor[0]}px`; div.style.top = `${p.y - icon.anchor[1]}px`; }
    };
    ov.onRemove = () => div.remove();
    ov.setMap(map);
    return ov;
  }, [position[0], position[1], icon.html]);
  return null;
};

const LPin = ({ position, icon, children }) => {
  const divIcon = useMemo(() => L.divIcon({ className: "", html: icon.html, iconSize: icon.size, iconAnchor: icon.anchor }), [icon.html, icon.size, icon.anchor]);
  return <Marker position={position} icon={divIcon}>{children && <Popup>{children}</Popup>}</Marker>;
};
export const Pin = (p) => (useG() ? <GPin {...p} /> : <LPin {...p} />);

const GLine = ({ positions, color = "#d4af37", weight = 5, opacity = 0.9 }) => {
  useGoogleObject(({ g, map }) => new g.maps.Polyline({ map, path: positions.map(([lat, lng]) => ({ lat, lng })), strokeColor: color, strokeWeight: weight, strokeOpacity: opacity }),
    [JSON.stringify(positions), color]);
  return null;
};
export const Line = (p) => (useG() ? <GLine {...p} /> : <Polyline positions={p.positions} pathOptions={{ color: p.color || "#d4af37", weight: p.weight || 5, opacity: p.opacity ?? 0.9 }} />);

// Fixed-pixel circle marker: { center, radius, fill, fillOpacity, stroke, strokeWeight, title, children (popup) }
const GDot = ({ center, radius, fill, fillOpacity = 0.9, stroke, strokeWeight = 0, title, children }) => {
  const content = useRef(children);
  content.current = children;
  useGoogleObject((ctx) => {
    const m = new ctx.g.maps.Marker({ map: ctx.map, position: { lat: center[0], lng: center[1] }, title,
      icon: { path: ctx.g.maps.SymbolPath.CIRCLE, scale: radius, fillColor: fill, fillOpacity, strokeColor: stroke || fill, strokeWeight } });
    if (content.current) m.addListener("click", () => openPopup(ctx, content.current, center));
    return m;
  }, [center[0], center[1], radius, fill, fillOpacity]);
  return null;
};
export const Dot = (p) => (useG() ? <GDot {...p} /> : (
  <CircleMarker center={p.center} radius={p.radius} pathOptions={{ stroke: Boolean(p.stroke), color: p.stroke, weight: p.strokeWeight || 0, fillColor: p.fill, fillOpacity: p.fillOpacity ?? 0.9 }}>
    {p.title && <Tooltip>{p.title}</Tooltip>}
    {p.children && <Popup>{p.children}</Popup>}
  </CircleMarker>
));

const LFit = ({ points, fitKey }) => {
  const map = useMap();
  useEffect(() => {
    if (points.length === 1) map.setView(points[0], 14);
    else if (points.length > 1) map.fitBounds(points, { padding: [40, 40], maxZoom: 15 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitKey, map]);
  return null;
};
const GFit = ({ points, fitKey }) => {
  const { g, map } = useG();
  useEffect(() => {
    if (points.length === 1) { map.setCenter({ lat: points[0][0], lng: points[0][1] }); map.setZoom(14); }
    else if (points.length > 1) {
      const b = new g.maps.LatLngBounds();
      points.forEach(([lat, lng]) => b.extend({ lat, lng }));
      map.fitBounds(b, 40);
      g.maps.event.addListenerOnce(map, "idle", () => map.getZoom() > 15 && map.setZoom(15));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitKey, map]);
  return null;
};
export const FitBounds = (p) => (useG() ? <GFit {...p} /> : <LFit {...p} />);

// Map click → onPick(lat, lng)
const GClick = ({ onPick }) => {
  const { map } = useG();
  const cb = useRef(onPick);
  cb.current = onPick;
  useEffect(() => { const l = map.addListener("click", (e) => cb.current(e.latLng.lat(), e.latLng.lng())); return () => l.remove(); }, [map]);
  return null;
};
const LClick = ({ onPick }) => { useMapEvents({ click: (e) => onPick(e.latlng.lat, e.latlng.lng) }); return null; };
export const MapClick = (p) => (useG() ? <GClick {...p} /> : <LClick {...p} />);

// Heat map: Google visualization HeatmapLayer, or graded circles on OSM. cells = [{lat, lng, count}]
const GHeat = ({ cells }) => {
  useGoogleObject(({ g, map }) => new g.maps.visualization.HeatmapLayer({ map, radius: 34, opacity: 0.75,
    data: cells.map((c) => ({ location: new g.maps.LatLng(c.lat, c.lng), weight: c.count })),
    gradient: ["rgba(212,175,55,0)", "rgba(253,230,138,0.6)", "rgba(245,158,11,0.8)", "rgba(239,68,68,0.95)"] }), [JSON.stringify(cells)]);
  return null;
};
const heatColor = (r) => (r > 0.66 ? "#ef4444" : r > 0.33 ? "#f59e0b" : "#fde68a");
export const Heat = ({ cells }) => {
  const ctx = useG();
  if (ctx) return <GHeat cells={cells} />;
  const max = Math.max(1, ...cells.map((c) => c.count));
  return cells.map((c) => {
    const r = Math.log1p(c.count) / Math.log1p(max);
    return <Dot key={`${c.lat},${c.lng}`} center={[c.lat, c.lng]} radius={8 + r * 26} fill={heatColor(r)} fillOpacity={0.25 + r * 0.45} title={`${c.count} pickups nearby`} />;
  });
};

// Places Autocomplete (Google) → [{address, place_id}], resolved to {address, lat, lng} on pick. Throws when unavailable so callers fall back.
export const placesSuggest = async (q) => {
  const g = await loadGoogle();
  const svc = new g.maps.places.AutocompleteService();
  const { predictions } = await svc.getPlacePredictions({ input: q, componentRestrictions: { country: "us" }, locationBias: { radius: 80000, center: { lat: DEFAULT_CENTER[0], lng: DEFAULT_CENTER[1] } } });
  return (predictions || []).slice(0, 5).map((p) => ({ address: p.description, place_id: p.place_id }));
};
export const placeDetails = async (placeId) => {
  const g = await loadGoogle();
  const { results } = await new g.maps.Geocoder().geocode({ placeId });
  const loc = results[0].geometry.location;
  return { address: results[0].formatted_address, lat: loc.lat(), lng: loc.lng(), source: "google" };
};

export const mapProvider = { name: GOOGLE_ENABLED ? "google" : "osm" };
